import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X, ChevronLeft, Loader2, ChevronDown, ChevronUp, ShieldCheck, Globe, Lock, PlayCircle, Eye, View, Sparkles, Flame, Snowflake, Armchair, WashingMachine, UtensilsCrossed, MoveVertical, DoorOpen, Trees, CircleParking, FileText, FileImage, RotateCcw, RotateCw, ImageUp } from "lucide-react";
import PhotoTour from "@/components/PhotoTour";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import CadastreSection from "@/components/cadastre/CadastreSection";
import DocumentsManager from "@/components/cadastre/DocumentsManager";
import { PUBLIC_SITE_URL } from "@/lib/publicUrl";

const PROPERTY_TYPES = [
    { v: "appartamento", l: "Appartamento" },
    { v: "villa", l: "Villa" },
    { v: "attico", l: "Attico" },
    { v: "loft", l: "Loft" },
    { v: "casa", l: "Casa" },
    { v: "cantina", l: "Cantina" },
    { v: "uffici", l: "Uffici" },
    { v: "locali_commerciali", l: "Locali commerciali" },
    { v: "box_auto", l: "Box auto" },
];
const RESIDENTIAL_TYPES = ["appartamento", "villa", "attico", "loft", "casa"];
const SHORT_STAY_CONTRACT_TYPES = ["casa_vacanze", "bb", "affitto_breve"];
const PRICE_PERIODS = [
    { v: "notte", l: "Notte" },
    { v: "settimana", l: "Settimana" },
    { v: "mese", l: "Mese" },
];
const CONTRACT_TYPES = [
    { v: "residenziale", l: "Residenziale" },
    { v: "transitorio", l: "Transitorio" },
    { v: "lungo_termine", l: "Lungo termine" },
    { v: "casa_vacanze", l: "Casa vacanze" },
    { v: "bb", l: "B&B" },
    { v: "affitto_breve", l: "Affitto breve" },
];
const CONDITIONS = [
    { v: "nuovo", l: "Nuovo" },
    { v: "ristrutturato", l: "Ristrutturato" },
    { v: "abitabile", l: "Abitabile" },
    { v: "da_ristrutturare", l: "Da ristrutturare" },
];
const ENERGY_CLASSES = ["A+", "A", "B", "C", "D", "E", "F", "G"];
const LEGACY_CONDITION_MAP = { nuova_costruzione: "nuovo", buono_stato: "abitabile" };
const HEATING_OPTIONS = [{ v: "autonomo", l: "Autonomo" }, { v: "centralizzato", l: "Centralizzato" }];
const GARDEN_OPTIONS = [{ v: "privato", l: "Privato" }, { v: "condominiale", l: "Condominiale" }, { v: "no", l: "Nessuno" }];
const PARKING_OPTIONS = [{ v: "box", l: "Box" }, { v: "coperto", l: "Coperto" }, { v: "scoperto", l: "Scoperto" }, { v: "no", l: "Nessuno" }];


// Compressione lato client prima dell'upload (max 1600px, JPEG 0.82) → payload più leggero e sito più veloce su mobile.
async function compressImage(file, maxSide = 1600, quality = 0.82) {
    if (!file.type.startsWith("image/") || file.size < 300 * 1024) return file;
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
        if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
        if (!blob || blob.size >= file.size) return file;
        return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    } catch {
        return file;
    }
}

const inputCls = "w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none transition-colors text-sm";

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">{label}</label>
        {children}
    </div>
);
const Section = ({ title, children }) => (
    <div className="border border-white/10 p-8">
        <div className="label-eyebrow mb-6">{title}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
    </div>
);

// Toggle Sì/No/Non specificato — stile neumorphic 3D con icona, usato per i
// campi Comfort/Struttura booleani (aria condizionata, ascensore, ecc).
const BoolToggle = ({ label, icon: Icon, value, onChange, testid }) => (
    <div>
        <div className="flex items-center gap-3 mb-2.5">
            <span className="feature-icon-badge"><Icon size={16} /></span>
            <label className="text-xs text-ink-400 tracking-wider uppercase">{label}</label>
        </div>
        <div className="flex gap-2">
            {[{ v: true, l: "Sì" }, { v: false, l: "No" }].map((opt) => (
                <button
                    key={String(opt.v)}
                    type="button"
                    data-testid={`${testid}-${opt.v ? "yes" : "no"}`}
                    onClick={() => onChange(value === opt.v ? null : opt.v)}
                    className={`feature-toggle-btn ${value === opt.v ? "is-active" : ""}`}
                >
                    {opt.l}
                </button>
            ))}
        </div>
    </div>
);

// Select con icona — stile neumorphic 3D, usato per i campi Comfort/Struttura
// a scelta multipla (riscaldamento, giardino, posto auto).
const IconSelect = ({ label, icon: Icon, value, onChange, options, testid }) => (
    <div>
        <div className="flex items-center gap-3 mb-2.5">
            <span className="feature-icon-badge"><Icon size={16} /></span>
            <label className="text-xs text-ink-400 tracking-wider uppercase">{label}</label>
        </div>
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="feature-select-neu w-full px-4 py-3 text-ink-50 outline-none transition-colors text-sm">
            <option value="" className="bg-ink-950">Non specificato</option>
            {options.map((o) => <option key={o.v} value={o.v} className="bg-ink-950">{o.l}</option>)}
        </select>
    </div>
);

export default function PropertyForm({ mode = "create", embedded = false, onSuccess }) {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const [submitting, setSubmitting] = useState(false);
    const [activatingPlan, setActivatingPlan] = useState(false);

    const activateBasePlan = async () => {
        setActivatingPlan(true);
        try {
            const { data } = await api.post("/subscription/checkout", {
                plan_id: "base", interval: "month", origin_url: PUBLIC_SITE_URL,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossibile avviare il pagamento, riprova.");
            setActivatingPlan(false);
        }
    };
    const [uploading, setUploading] = useState(false);
    const [floorplanUploading, setFloorplanUploading] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(mode === "edit");
    const [cadastreDraft, setCadastreDraft] = useState({});
    const [pendingDocs, setPendingDocs] = useState([]);
    const [docVersion, setDocVersion] = useState(0);
    const [docsOpen, setDocsOpen] = useState(true);
    const [privacyBusy, setPrivacyBusy] = useState(false);
    const [photoTourPreview, setPhotoTourPreview] = useState(false);
    const [aiGenerating, setAiGenerating] = useState(false);
    const [sellerLeads, setSellerLeads] = useState([]);

    useEffect(() => {
        if (user?.role !== "privato") {
            api.get("/crm/leads?kind=seller").then((r) => setSellerLeads(r.data.items || [])).catch(() => {});
        }
    }, [user]);

    // Embed URL parser (identico a PropertyDetail.jsx)
    const getEmbedUrl = (url) => {
        if (!url) return null;
        try {
            const u = new URL(url);
            if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) {
                return `https://www.youtube.com/embed/${u.searchParams.get("v")}?rel=0`;
            }
            if (u.hostname === "youtu.be") {
                const id = u.pathname.replace("/", "");
                if (id) return `https://www.youtube.com/embed/${id}?rel=0`;
            }
            if (u.hostname.includes("youtube.com") && u.pathname.startsWith("/embed/")) return url;
            if (u.hostname.includes("vimeo.com") && !u.hostname.includes("player")) {
                const id = u.pathname.split("/").filter(Boolean)[0];
                if (id) return `https://player.vimeo.com/video/${id}`;
            }
            return url;
        } catch { return null; }
    };

    const [form, setForm] = useState({
        title: "", description: "", price: "", price_period: "", property_type: "appartamento",
        transaction_type: "vendita", contract_type: "", condition: "",
        rooms: 2, bathrooms: 1, surface_sqm: 80,
        floor: "", city: "", region: "", zone: "", address: "", zip_code: "", listing_phone: user?.phone || "",
        latitude: null, longitude: null,
        photos: [], floorplan_file_id: "", floorplan_url: "", floorplan_filename: "", floorplan_content_type: "",
        features: [], energy_class: "", owner_reference: "", seller_lead_id: "",
        video_url: "", virtual_tour_url: "", photo_tour_enabled: false,
        ical_url: "", booking_url: "",
        heating: "", air_conditioning: null, furnished: null, washing_machine: null, dishwasher: null,
        elevator: null, balcony: null, garden: "", parking: "",
    });

    useEffect(() => {
        if (mode === "edit" && id) {
            api.get(`/properties/${id}`).then((r) => {
                const p = r.data;
                setForm({
                    title: p.title || "", description: p.description || "",
                    price: p.price || "", price_period: p.price_period || "", property_type: p.property_type || "appartamento",
                    transaction_type: p.transaction_type || "vendita",
                    contract_type: p.contract_type || "",
                    condition: LEGACY_CONDITION_MAP[p.condition] || p.condition || "",
                    rooms: p.rooms || 2, bathrooms: p.bathrooms || 1,
                    surface_sqm: p.surface_sqm || 80, floor: p.floor || "",
                    city: p.city || "", region: p.region || "", zone: p.zone || "",
                    address: p.address || "", zip_code: p.zip_code || "", listing_phone: p.listing_phone || p.owner_phone || user?.phone || "",
                    latitude: p.latitude ?? null, longitude: p.longitude ?? null,
                    photos: p.photos || [], features: p.features || [],
                    floorplan_file_id: p.floorplan_file_id || "", floorplan_url: p.floorplan_url || "",
                    floorplan_filename: p.floorplan_filename || "", floorplan_content_type: p.floorplan_content_type || "",
                    energy_class: p.energy_class || "", owner_reference: p.owner_reference || "", seller_lead_id: p.seller_lead_id || "",
                    video_url: p.video_url || "", virtual_tour_url: p.virtual_tour_url || "",
                    ical_url: p.ical_url || "", booking_url: p.booking_url || "",
                    photo_tour_enabled: !!p.photo_tour_enabled,
                    heating: p.heating || "", air_conditioning: p.air_conditioning ?? null,
                    furnished: p.furnished ?? null, washing_machine: p.washing_machine ?? null,
                    dishwasher: p.dishwasher ?? null, elevator: p.elevator ?? null,
                    balcony: p.balcony ?? null, garden: p.garden || "", parking: p.parking || "",
                });
            }).finally(() => setLoading(false));
        }
    }, [mode, id, user?.phone]);

    useEffect(() => {
        if (mode !== "edit" && user?.phone) {
            setForm((current) => current.listing_phone ? current : { ...current, listing_phone: user.phone });
        }
    }, [mode, user?.phone]);

    const handleChange = (k, v) => setForm({ ...form, [k]: v });
    const isShortStay = form.transaction_type === "affitto" && SHORT_STAY_CONTRACT_TYPES.includes(form.contract_type);
    const handleTransactionChange = (v) => {
        setForm((current) => (
            v === "affitto" ? { ...current, transaction_type: v } : { ...current, transaction_type: v, contract_type: "", price_period: "", ical_url: "", booking_url: "" }
        ));
    };

    const uploadPhoto = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true); setError(null);
        const uploadedUrls = [];
        const failures = [];
        try {
            for (const file of files) {
                try {
                    const fd = new FormData();
                    fd.append("file", await compressImage(file));
                    const { data } = await api.post("/upload/property-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    uploadedUrls.push(data.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url);
                } catch (uploadError) {
                    failures.push(uploadError?.response?.data?.detail || file.name);
                }
            }
            if (uploadedUrls.length) setForm((f) => ({ ...f, photos: [...f.photos, ...uploadedUrls] }));
            if (failures.length) setError(`${failures.length} foto non caricate. Riprova con file immagine validi.`);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };
    const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

    // Agenzia Pro / Agenzia Full: watermark logo agenzia + rotazione foto nella sezione upload.
    const isAgencyPro = user?.role === "agenzia" && user?.plan_active && (user?.plan_id === "premium" || user?.plan_id === "agenzia");
    const [rotatingIndex, setRotatingIndex] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);

    const rotatePhoto = async (index, direction) => {
        if (rotatingIndex !== null) return;
        setRotatingIndex(index);
        try {
            const { data } = await api.post("/upload/property-photo/rotate", { url: form.photos[index], direction });
            setForm((f) => ({ ...f, photos: f.photos.map((u, i) => (i === index ? data.url : u)) }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impossibile ruotare la foto");
        } finally {
            setRotatingIndex(null);
        }
    };

    const uploadAgencyLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/upload/agency-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await refresh();
            toast.success("Logo caricato: verrà applicato come watermark sulle prossime foto");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nel caricamento del logo");
        } finally {
            setLogoUploading(false);
            e.target.value = "";
        }
    };

    const [applyingWatermark, setApplyingWatermark] = useState(false);
    const [watermarkApplied, setWatermarkApplied] = useState(false);

    const applyAgencyWatermarkToExistingPhotos = async () => {
        if (!form.photos.length) return;
        setApplyingWatermark(true);
        try {
            const { data } = await api.post("/upload/property-photo/apply-agency-watermark", { urls: form.photos });
            const byOld = new Map((data.results || []).map((r) => [r.old, r.new]));
            setForm((f) => ({ ...f, photos: f.photos.map((u) => byOld.get(u) || u) }));
            const failed = (data.results || []).filter((r) => !r.ok).length;
            if (failed) toast.error(`${failed} foto non aggiornate, riprova`);
            else toast.success("Watermark del logo applicato alle foto esistenti");
            setWatermarkApplied(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nell'applicare il watermark alle foto esistenti");
        } finally {
            setApplyingWatermark(false);
        }
    };

    const uploadFloorplan = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFloorplanUploading(true); setError(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/property-floorplan", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = data.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url;
            setForm((current) => ({
                ...current,
                floorplan_file_id: data.file_id,
                floorplan_url: url,
                floorplan_filename: data.filename,
                floorplan_content_type: data.content_type,
            }));
        } catch (uploadError) {
            setError(uploadError?.response?.data?.detail || "Errore durante il caricamento della planimetria");
        } finally {
            setFloorplanUploading(false);
            e.target.value = "";
        }
    };
    const removeFloorplan = () => setForm((current) => ({
        ...current, floorplan_file_id: "", floorplan_url: "", floorplan_filename: "", floorplan_content_type: "",
    }));

    const togglePrivacy = async (nextValue) => {
        if (privacyBusy) return;
        setPrivacyBusy(true);
        try {
            await api.put("/auth/profile", { contacts_public: !!nextValue });
            await refresh();
        } catch { /* silent */ }
        finally { setPrivacyBusy(false); }
    };

    const generateAiDescription = async () => {
        if (!form.title.trim() || !form.city.trim()) {
            toast.error("Inserisci almeno titolo e città prima di generare la descrizione");
            return;
        }
        setAiGenerating(true);
        try {
            const { data } = await api.post("/properties/ai-description", {
                title: form.title, property_type: form.property_type, transaction_type: form.transaction_type,
                price: form.price, surface_sqm: form.surface_sqm, rooms: form.rooms, bathrooms: form.bathrooms,
                city: form.city, region: form.region, floor: form.floor, condition: form.condition, energy_class: form.energy_class,
            });
            handleChange("description", data.description);
            toast.success("Descrizione generata con l'AI");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generazione non disponibile, riprova");
        } finally {
            setAiGenerating(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null); setSubmitting(true);
        try {
            const payload = {
                ...form,
                price: parseFloat(form.price),
                rooms: parseInt(form.rooms),
                bathrooms: parseInt(form.bathrooms),
                surface_sqm: parseInt(form.surface_sqm),
                contract_type: form.transaction_type === "affitto" ? (form.contract_type || null) : null,
                price_period: isShortStay ? (form.price_period || null) : null,
                ical_url: isShortStay ? (form.ical_url?.trim() || null) : null,
                booking_url: isShortStay ? (form.booking_url?.trim() || null) : null,
                condition: form.condition || null,
                zone: form.zone?.trim() || null,
                heating: form.heating || null,
                garden: form.garden || null,
                parking: form.parking || null,
            };
            // Se l'utente ha selezionato un suggerimento di indirizzo, lat/lng sono già note e precise;
            // altrimenti prova il geocode silenzioso come fallback (comportamento preesistente).
            if (form.latitude != null && form.longitude != null) {
                payload.latitude = form.latitude;
                payload.longitude = form.longitude;
            } else {
                try {
                    const q = `${form.address}, ${form.city}, Italia`;
                    const { data } = await api.get("/geocode", { params: { q } });
                    if (data.found) { payload.latitude = data.lat; payload.longitude = data.lng; }
                } catch { /* ignore */ }
            }

            if (mode === "edit") {
                await api.put(`/properties/${id}`, payload);
                if (onSuccess) onSuccess(id); else navigate(`/annunci/${id}`);
            } else {
                const { data } = await api.post("/properties", payload);
                // Salva dati catastali e documenti raccolti durante la creazione
                if (Object.values(cadastreDraft).some((v) => v)) {
                    try { await api.put(`/properties/${data.id}/cadastre`, cadastreDraft); } catch { /* silent */ }
                }
                for (const p of pendingDocs) {
                    const fd = new FormData();
                    fd.append("file", p.file);
                    fd.append("doc_type", p.doc_type);
                    try { await api.post(`/properties/${data.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch { /* silent */ }
                }
                if (onSuccess) onSuccess(data.id); else navigate(`/annunci/${data.slug || data.id}`);
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Errore");
        } finally {
            setSubmitting(false);
        }
    };

    if (!user) return <div className="pt-32 text-center text-ink-400">Accedi per continuare. <Link to="/login" className="text-gold">Login</Link></div>;
    if (mode === "create" && !user.plan_active) {
        if (user.role === "privato") {
            return (
                <div data-testid="pubblica-need-plan" className="pt-32 max-w-xl mx-auto px-6 text-center">
                    <span className="label-eyebrow">Free · Solo lettura</span>
                    <h1 className="font-serif text-3xl sm:text-4xl text-ink-50 mt-4 leading-snug">
                        Vuoi vendere casa? Attiva il piano Base a 9,99€/mese per pubblicare il tuo annuncio.
                    </h1>
                    <p className="text-ink-400 mt-4 mb-8 text-sm">Nessun impegno, disattivabile in ogni momento. Lo sblocco della pubblicazione avviene subito dopo la conferma del pagamento.</p>
                    <button
                        data-testid="pubblica-activate-base-btn"
                        onClick={activateBasePlan}
                        disabled={activatingPlan}
                        className="px-8 py-4 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors inline-block disabled:opacity-50"
                    >
                        {activatingPlan ? "Attivazione..." : "Attiva Ora"}
                    </button>
                </div>
            );
        }
        return (
            <div data-testid="pubblica-need-plan" className="pt-32 max-w-2xl mx-auto px-6 text-center">
                <span className="label-eyebrow">Abbonamento richiesto</span>
                <h1 className="font-serif text-4xl text-ink-50 mt-4">Sblocca la pubblicazione</h1>
                <p className="text-ink-400 mt-4 mb-8">Per pubblicare un annuncio è necessario un abbonamento attivo.</p>
                <Link to="/prezzi" data-testid="pubblica-go-pricing" className="px-8 py-4 bg-gold text-black text-sm font-medium hover:bg-gold-soft transition-colors inline-block">Vedi gli abbonamenti</Link>
            </div>
        );
    }

    if (loading) return <div className="pt-32 text-ink-400 text-center">Caricamento...</div>;

    return (
        <div data-testid={`${mode}-page`} className={embedded ? "pb-8" : "pt-32 pb-24 w-full px-6 md:px-12 lg:px-20 2xl:px-28"}>
            {!embedded && mode === "edit" && (
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-ink-400 hover:text-gold mb-6"><ChevronLeft size={14} /> Torna alla dashboard</Link>
            )}
            {!embedded && <span className="label-eyebrow">{mode === "edit" ? "Modifica annuncio" : "Nuovo annuncio"}</span>}
            {!embedded && <h1 className="font-serif text-5xl text-ink-50 mt-3 mb-12">{mode === "edit" ? "Aggiorna il tuo immobile" : "Pubblica il tuo immobile"}</h1>}

            <form onSubmit={submit} className={embedded ? "space-y-10" : "space-y-10 max-w-5xl mx-auto"}>
                <Section title="Identità">
                    <Field label="Titolo dell'annuncio">
                        <input data-testid="form-title" required value={form.title} onChange={(e) => handleChange("title", e.target.value)} className={inputCls} placeholder="Es. Attico panoramico in Brera" />
                    </Field>
                    {user?.role !== "privato" && (
                        <Field label="Associa Venditore (Pipeline)">
                            <select data-testid="form-seller-lead" value={form.seller_lead_id} onChange={(e) => {
                                const leadId = e.target.value;
                                const lead = sellerLeads.find((l) => l.id === leadId);
                                setForm((f) => ({ ...f, seller_lead_id: leadId, owner_reference: lead ? lead.name : f.owner_reference }));
                            }} className={inputCls}>
                                <option value="">— Nessun collegamento —</option>
                                {sellerLeads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                            <p className="text-[11px] text-ink-500 mt-1.5">Collega questo annuncio a un contatto della Pipeline Venditori: resta un dato interno, mai visibile al pubblico.</p>
                        </Field>
                    )}
                    <Field label="Proprietario (Riferimento Interno)">
                        <input data-testid="form-owner-reference" value={form.owner_reference} onChange={(e) => handleChange("owner_reference", e.target.value)} className={inputCls} placeholder="Es. Sig. Rossi" />
                        <p className="text-[11px] text-ink-500 mt-1.5">Solo per tuo riordino interno: non viene mai mostrato sull'annuncio pubblico.</p>
                    </Field>
                    <Field label="Descrizione" className="md:col-span-2">
                        <div className="flex items-center justify-between mb-2">
                            <button type="button" data-testid="ai-generate-description-btn" onClick={generateAiDescription} disabled={aiGenerating}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                                {aiGenerating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                                {aiGenerating ? "Generazione in corso..." : "Genera descrizione con AI"}
                            </button>
                        </div>
                        <textarea data-testid="form-description" required rows={5} value={form.description} onChange={(e) => handleChange("description", e.target.value)} className={inputCls} />
                    </Field>
                </Section>

                <Section title="Caratteristiche">
                    <Field label="Tipologia">
                        <select data-testid="form-type" value={form.property_type} onChange={(e) => handleChange("property_type", e.target.value)} className={inputCls}>
                            {PROPERTY_TYPES.map((t) => <option key={t.v} value={t.v} className="bg-ink-950">{t.l}</option>)}
                        </select>
                    </Field>
                    <Field label="Operazione">
                        <select data-testid="form-transaction" value={form.transaction_type} onChange={(e) => handleTransactionChange(e.target.value)} className={inputCls}>
                            <option value="vendita" className="bg-ink-950">Vendita</option>
                            <option value="affitto" className="bg-ink-950">Affitto</option>
                        </select>
                    </Field>
                    {form.transaction_type === "affitto" && (
                        <Field label="Tipologia contratto">
                            <select data-testid="form-contract-type" value={form.contract_type} onChange={(e) => handleChange("contract_type", e.target.value)} className={inputCls}>
                                <option value="" className="bg-ink-950">Non specificato</option>
                                {CONTRACT_TYPES.map((c) => <option key={c.v} value={c.v} className="bg-ink-950">{c.l}</option>)}
                            </select>
                        </Field>
                    )}
                    <Field label="Stato dell'immobile">
                        <select data-testid="form-condition" value={form.condition} onChange={(e) => handleChange("condition", e.target.value)} className={inputCls}>
                            <option value="" className="bg-ink-950">Non specificato</option>
                            {CONDITIONS.map((c) => <option key={c.v} value={c.v} className="bg-ink-950">{c.l}</option>)}
                        </select>
                    </Field>
                    {isShortStay ? (
                        <>
                            <Field label="Prezzo a partire da (€)"><input data-testid="form-price" required type="number" value={form.price} onChange={(e) => handleChange("price", e.target.value)} className={inputCls} /></Field>
                            <Field label="Per">
                                <select data-testid="form-price-period" value={form.price_period} onChange={(e) => handleChange("price_period", e.target.value)} className={inputCls}>
                                    <option value="" className="bg-ink-950">Seleziona</option>
                                    {PRICE_PERIODS.map((p) => <option key={p.v} value={p.v} className="bg-ink-950">{p.l}</option>)}
                                </select>
                            </Field>
                        </>
                    ) : (
                        <Field label="Prezzo (€)"><input data-testid="form-price" required type="number" value={form.price} onChange={(e) => handleChange("price", e.target.value)} className={inputCls} /></Field>
                    )}
                    <Field label="Superficie (m²)"><input data-testid="form-surface" required type="number" value={form.surface_sqm} onChange={(e) => handleChange("surface_sqm", e.target.value)} className={inputCls} /></Field>
                    <Field label="Locali"><input data-testid="form-rooms" required type="number" value={form.rooms} onChange={(e) => handleChange("rooms", e.target.value)} className={inputCls} /></Field>
                    <Field label="Bagni"><input data-testid="form-bathrooms" required type="number" value={form.bathrooms} onChange={(e) => handleChange("bathrooms", e.target.value)} className={inputCls} /></Field>
                    <Field label="Piano"><input data-testid="form-floor" value={form.floor} onChange={(e) => handleChange("floor", e.target.value)} className={inputCls} /></Field>
                    <Field label={`Classe energetica${RESIDENTIAL_TYPES.includes(form.property_type) ? " *" : ""}`}>
                        <select data-testid="form-energy" required={RESIDENTIAL_TYPES.includes(form.property_type)} value={form.energy_class} onChange={(e) => handleChange("energy_class", e.target.value)} className={inputCls}>
                            <option value="" className="bg-ink-950">Non specificato</option>
                            {ENERGY_CLASSES.map((c) => <option key={c} value={c} className="bg-ink-950">{c}</option>)}
                        </select>
                    </Field>
                </Section>

                {RESIDENTIAL_TYPES.includes(form.property_type) && (
                    <div className="feature-card-neu p-8">
                        <div className="label-eyebrow mb-6">Comfort</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <IconSelect testid="form-heating" label="Riscaldamento" icon={Flame} value={form.heating} onChange={(v) => handleChange("heating", v)} options={HEATING_OPTIONS} />
                            <BoolToggle testid="form-ac" label="Aria condizionata" icon={Snowflake} value={form.air_conditioning} onChange={(v) => handleChange("air_conditioning", v)} />
                            <BoolToggle testid="form-furnished" label="Arredamento" icon={Armchair} value={form.furnished} onChange={(v) => handleChange("furnished", v)} />
                            <BoolToggle testid="form-washing-machine" label="Lavatrice" icon={WashingMachine} value={form.washing_machine} onChange={(v) => handleChange("washing_machine", v)} />
                            <BoolToggle testid="form-dishwasher" label="Lavastoviglie" icon={UtensilsCrossed} value={form.dishwasher} onChange={(v) => handleChange("dishwasher", v)} />
                        </div>
                    </div>
                )}

                {RESIDENTIAL_TYPES.includes(form.property_type) && (
                    <div className="feature-card-neu p-8">
                        <div className="label-eyebrow mb-6">Struttura</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <BoolToggle testid="form-elevator" label="Ascensore" icon={MoveVertical} value={form.elevator} onChange={(v) => handleChange("elevator", v)} />
                            <BoolToggle testid="form-balcony" label="Balcone/Terrazzo" icon={DoorOpen} value={form.balcony} onChange={(v) => handleChange("balcony", v)} />
                            <IconSelect testid="form-garden" label="Giardino" icon={Trees} value={form.garden} onChange={(v) => handleChange("garden", v)} options={GARDEN_OPTIONS} />
                            <IconSelect testid="form-parking" label="Posto auto" icon={CircleParking} value={form.parking} onChange={(v) => handleChange("parking", v)} options={PARKING_OPTIONS} />
                        </div>
                    </div>
                )}

                <Section title="Localizzazione">
                    <Field label="Città"><input data-testid="form-city" required value={form.city} onChange={(e) => handleChange("city", e.target.value)} className={inputCls} /></Field>
                    <Field label="Regione"><input data-testid="form-region" required value={form.region} onChange={(e) => handleChange("region", e.target.value)} className={inputCls} /></Field>
                    <Field label="Zona / Quartiere (pubblico, es. Navigli)" className="md:col-span-2">
                        <input data-testid="form-zone" value={form.zone} onChange={(e) => handleChange("zone", e.target.value)} placeholder="Facoltativo — usato nel titolo e nell'URL dell'annuncio, senza rivelare l'indirizzo" className={inputCls} />
                    </Field>
                    <Field label="Indirizzo" className="md:col-span-2">
                        <AddressAutocomplete
                            dataTestId="form-address"
                            required
                            value={form.address}
                            onChange={(text) => setForm((current) => ({ ...current, address: text, latitude: null, longitude: null }))}
                            onSelect={(s) => setForm((current) => ({
                                ...current,
                                address: s.address,
                                latitude: s.latitude,
                                longitude: s.longitude,
                                city: current.city?.trim() ? current.city : (s.city || current.city),
                                zip_code: current.zip_code?.trim() ? current.zip_code : (s.zip_code || current.zip_code),
                            }))}
                            placeholder="Inizia a digitare la via..."
                            className={inputCls}
                        />
                        <p className="text-[11px] text-ink-400 mt-1.5">Scegli un suggerimento per salvare la posizione esatta sulla mappa dell&apos;annuncio.</p>
                    </Field>
                    <Field label="CAP"><input data-testid="form-zip" value={form.zip_code} onChange={(e) => handleChange("zip_code", e.target.value)} className={inputCls} /></Field>
                    <Field label="Numero di telefono per questo annuncio" className="md:col-span-2">
                        <input data-testid="form-listing-phone" type="tel" autoComplete="tel" value={form.listing_phone} onChange={(e) => handleChange("listing_phone", e.target.value)} placeholder="+39 333 123 4567" className={inputCls} />
                    </Field>
                </Section>

                <Section title="Foto (con watermark vendidirect.it)">
                    <div className="md:col-span-2 space-y-4">
                        {isAgencyPro && !user?.agency_logo && (
                            <div data-testid="agency-logo-upload-banner" className="border border-gold/40 bg-gold/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                <ImageUp size={20} className="text-gold shrink-0" />
                                <p className="text-xs text-ink-200 flex-1">Carica il logo della tua agenzia: verrà applicato come watermark in basso a destra su tutte le foto dei tuoi annunci.</p>
                                <label className={`inline-flex items-center gap-2 px-4 py-2 border border-gold text-gold hover:bg-gold hover:text-black cursor-pointer transition-colors text-xs font-medium ${logoUploading ? "opacity-50" : ""}`}>
                                    {logoUploading ? <Loader2 size={13} className="animate-spin" /> : <ImageUp size={13} />}
                                    {logoUploading ? "Caricamento..." : "Carica logo agenzia"}
                                    <input data-testid="agency-logo-file" type="file" accept="image/*" onChange={uploadAgencyLogo} className="hidden" disabled={logoUploading} />
                                </label>
                            </div>
                        )}
                        {isAgencyPro && user?.agency_logo && form.photos.length > 0 && !watermarkApplied && (
                            <div data-testid="agency-watermark-regenerate-banner" className="border border-gold/40 bg-gold/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                                <ImageUp size={20} className="text-gold shrink-0" />
                                <p className="text-xs text-ink-200 flex-1">Applica il logo della tua agenzia come watermark anche alle foto già caricate in questo annuncio.</p>
                                <button
                                    type="button"
                                    data-testid="apply-agency-watermark-existing"
                                    disabled={applyingWatermark}
                                    onClick={applyAgencyWatermarkToExistingPhotos}
                                    className={`inline-flex items-center gap-2 px-4 py-2 border border-gold text-gold hover:bg-gold hover:text-black cursor-pointer transition-colors text-xs font-medium ${applyingWatermark ? "opacity-50" : ""}`}
                                >
                                    {applyingWatermark ? <Loader2 size={13} className="animate-spin" /> : <ImageUp size={13} />}
                                    {applyingWatermark ? "Applicazione..." : "Applica watermark alle foto esistenti"}
                                </button>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-3">
                            {form.photos.map((url, i) => (
                                <div key={i} className="relative w-28 h-28 border border-white/10">
                                    <img data-testid={`photo-preview-${i}`} src={url} alt={`Foto immobile ${i + 1}`} className="w-full h-full object-cover" />
                                    <button data-testid={`remove-photo-${i}`} aria-label={`Rimuovi foto ${i + 1}`} type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-black border border-gold text-gold rounded-full p-1"><X size={10} /></button>
                                    {isAgencyPro && (
                                        <div className="absolute bottom-0 inset-x-0 flex items-center justify-center gap-1 bg-black/60 py-1">
                                            <button
                                                data-testid={`rotate-left-${i}`}
                                                aria-label={`Ruota foto ${i + 1} a sinistra`}
                                                type="button"
                                                disabled={rotatingIndex !== null}
                                                onClick={() => rotatePhoto(i, "left")}
                                                className="text-ink-50 hover:text-gold p-1 disabled:opacity-40"
                                            >
                                                {rotatingIndex === i ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                                            </button>
                                            <button
                                                data-testid={`rotate-right-${i}`}
                                                aria-label={`Ruota foto ${i + 1} a destra`}
                                                type="button"
                                                disabled={rotatingIndex !== null}
                                                onClick={() => rotatePhoto(i, "right")}
                                                className="text-ink-50 hover:text-gold p-1 disabled:opacity-40"
                                            >
                                                {rotatingIndex === i ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <label className={`inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-ink-50 hover:border-gold cursor-pointer transition-colors text-sm ${uploading ? "opacity-50" : ""}`}>
                            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                            {uploading ? "Caricamento foto..." : "Carica una o più foto"}
                            <input data-testid="form-photo-file" type="file" accept="image/*" multiple onChange={uploadPhoto} className="hidden" disabled={uploading} />
                        </label>
                        <p className="text-xs text-ink-400">
                            Verrà applicato automaticamente un watermark centrato con il logo vendidirect.it.
                            {isAgencyPro && user?.agency_logo && " Il logo della tua agenzia viene inoltre applicato in basso a destra su ogni foto."}
                        </p>
                    </div>
                </Section>

                <Section title="Planimetria catastale">
                    <div className="md:col-span-2 space-y-4" data-testid="floorplan-upload-section">
                        {form.floorplan_url ? (
                            <div data-testid="floorplan-uploaded-preview" className="flex flex-col sm:flex-row sm:items-center gap-4 border border-gold/30 bg-gold/5 p-4">
                                <span className="w-12 h-12 shrink-0 grid place-items-center border border-gold/30 text-gold">
                                    {form.floorplan_content_type === "application/pdf" ? <FileText size={22} /> : <FileImage size={22} />}
                                </span>
                                <div className="min-w-0 flex-1"><div className="text-sm text-ink-50 truncate">{form.floorplan_filename || "Planimetria"}</div><div className="text-xs text-ink-400 mt-1">{form.floorplan_content_type === "application/pdf" ? "Documento PDF" : "Immagine planimetria"}</div></div>
                                <button data-testid="remove-floorplan-button" type="button" onClick={removeFloorplan} className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs"><X size={13} /> Rimuovi</button>
                            </div>
                        ) : (
                            <label data-testid="floorplan-upload-button" className={`inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-ink-50 hover:border-gold cursor-pointer transition-colors text-sm ${floorplanUploading ? "opacity-50" : ""}`}>
                                {floorplanUploading ? <Loader2 size={14} className="animate-spin" /> : <FileImage size={14} />}
                                {floorplanUploading ? "Caricamento planimetria..." : "Carica planimetria catastale"}
                                <input data-testid="form-floorplan-file" type="file" accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf" onChange={uploadFloorplan} className="hidden" disabled={floorplanUploading} />
                            </label>
                        )}
                        <p className="text-xs text-ink-400">Un solo file JPG, PNG, WEBP o PDF, massimo 10 MB. Sarà mostrato separatamente dalla galleria fotografica.</p>
                    </div>
                </Section>

                <Section title="Video e Virtual Tour (opzionali)">
                    <Field label="Link Video (YouTube/Vimeo)" className="md:col-span-2">
                        <input
                            data-testid="form-video-url"
                            type="url"
                            value={form.video_url}
                            onChange={(e) => handleChange("video_url", e.target.value)}
                            placeholder="https://www.youtube.com/watch?v=... oppure https://vimeo.com/..."
                            className={inputCls}
                        />
                        <p className="text-[11px] text-ink-400 mt-1.5">
                            Incolla qui l&apos;URL completo del tuo video per mostrarlo direttamente nell&apos;annuncio.
                        </p>
                        {getEmbedUrl(form.video_url) && (
                            <div data-testid="video-preview" className="mt-3 border border-gold/30 bg-black/30 p-3">
                                <div className="flex items-center gap-2 mb-2 text-[11px] text-gold uppercase tracking-widest">
                                    <Eye size={12} /> Anteprima video · verifica prima di pubblicare
                                </div>
                                <div className="aspect-video max-w-md w-full bg-black overflow-hidden rounded-sm">
                                    <iframe
                                        src={getEmbedUrl(form.video_url)}
                                        title="Anteprima video"
                                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="w-full h-full border-0"
                                    />
                                </div>
                            </div>
                        )}
                    </Field>
                    <Field label="Link Virtual Tour (Matterport/3D)" className="md:col-span-2">
                        <input
                            data-testid="form-virtual-tour-url"
                            type="url"
                            value={form.virtual_tour_url}
                            onChange={(e) => handleChange("virtual_tour_url", e.target.value)}
                            placeholder="https://my.matterport.com/show/?m=... oppure altro link 3D"
                            className={inputCls}
                        />
                        <p className="text-[11px] text-ink-400 mt-1.5">
                            Incolla qui l&apos;URL completo del tour 3D per mostrarlo direttamente nell&apos;annuncio.
                        </p>
                        {form.virtual_tour_url && form.virtual_tour_url.startsWith("http") && (
                            <div data-testid="tour-preview" className="mt-3 border border-blue-500/30 bg-black/30 p-3">
                                <div className="flex items-center gap-2 mb-2 text-[11px] text-blue-300 uppercase tracking-widest">
                                    <View size={12} /> Anteprima virtual tour · verifica prima di pubblicare
                                </div>
                                <div className="aspect-video max-w-md w-full bg-black overflow-hidden rounded-sm">
                                    <iframe
                                        src={form.virtual_tour_url}
                                        title="Anteprima virtual tour"
                                        allow="fullscreen; xr-spatial-tracking; gyroscope; accelerometer; vr"
                                        allowFullScreen
                                        className="w-full h-full border-0"
                                    />
                                </div>
                            </div>
                        )}
                    </Field>

                    {/* Photo Tour automatico — visibile solo se non c'è già un Matterport e almeno 3 foto */}
                    <div className="md:col-span-2 mt-4 border-2 border-gold/40 bg-gradient-to-br from-gold/10 to-gold/5 p-5">
                        <div className="flex items-start gap-3">
                            <div className="shrink-0 w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                                <Sparkles size={18} className="text-gold" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-serif text-lg text-ink-50">Tour automatico dalle tue foto</span>
                                    <span className="text-[10px] uppercase tracking-widest bg-gold/25 text-gold px-2 py-0.5 rounded-full">Gratis · Nessun Matterport</span>
                                </div>
                                <p className="text-sm text-ink-200 leading-relaxed mt-2">
                                    Se non hai un tour 3D professionale, possiamo creare un tour cinematografico automatico dalle foto che hai caricato:
                                    zoom fluidi, transizioni eleganti, autoplay. <b>Guarda l&apos;anteprima e attivalo solo se ti piace.</b>
                                </p>
                                <div className="flex flex-wrap items-center gap-3 mt-4">
                                    <button
                                        data-testid="photo-tour-preview-btn"
                                        type="button"
                                        onClick={() => setPhotoTourPreview(true)}
                                        disabled={form.photos.length < 3}
                                        className="inline-flex items-center gap-2 px-4 py-2.5 border border-gold text-gold hover:bg-gold hover:text-black transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        <PlayCircle size={16} /> Anteprima tour
                                        {form.photos.length < 3 && <span className="text-[10px] text-ink-400">(min 3 foto)</span>}
                                    </button>
                                    <label className="inline-flex items-center gap-2 cursor-pointer group">
                                        <span className="text-sm text-ink-100">Attiva nel mio annuncio</span>
                                        <span
                                            data-testid="photo-tour-toggle"
                                            role="switch"
                                            aria-checked={form.photo_tour_enabled}
                                            onClick={() => handleChange("photo_tour_enabled", !form.photo_tour_enabled)}
                                            className={`relative w-12 h-7 rounded-full transition-colors ${form.photo_tour_enabled ? "bg-gold" : "bg-ink-700"} ${form.virtual_tour_url ? "opacity-40 pointer-events-none" : ""}`}
                                        >
                                            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform ${form.photo_tour_enabled ? "translate-x-5" : "translate-x-0"}`} />
                                        </span>
                                    </label>
                                </div>
                                {form.virtual_tour_url && (
                                    <p className="text-[11px] text-ink-400 mt-3 italic">
                                        Hai già un Virtual Tour 3D professionale: il tour automatico è disattivato per evitare doppioni.
                                    </p>
                                )}
                                {!form.virtual_tour_url && form.photo_tour_enabled && (
                                    <p className="text-[11px] text-gold mt-3">
                                        Attivo: nel tuo annuncio comparirà il pulsante &quot;Tour dell&apos;immobile&quot; con transizioni cinematiche.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </Section>

                {isShortStay && (
                    <Section title="Disponibilità e prenotazione (opzionali)">
                        <Field label="Link disponibilità (iCal)" className="md:col-span-2">
                            <input
                                data-testid="form-ical-url"
                                type="url"
                                value={form.ical_url}
                                onChange={(e) => handleChange("ical_url", e.target.value)}
                                placeholder="https://.../calendario.ics"
                                className={inputCls}
                            />
                            <p className="text-[11px] text-ink-400 mt-1.5">
                                Incolla qui il link iCal del tuo channel manager (es. Airbnb, Booking.com, Smoobu): sull&apos;annuncio comparirà il calendario con le date occupate.
                            </p>
                        </Field>
                        <Field label="Link prenotazione" className="md:col-span-2">
                            <input
                                data-testid="form-booking-url"
                                type="url"
                                value={form.booking_url}
                                onChange={(e) => handleChange("booking_url", e.target.value)}
                                placeholder="https://.../prenota"
                                className={inputCls}
                            />
                            <p className="text-[11px] text-ink-400 mt-1.5">
                                Incolla qui il link del tuo booking engine: sull&apos;annuncio comparirà un pulsante &quot;Prenota ora&quot;.
                            </p>
                        </Field>
                    </Section>
                )}

                <Section title="Documentazione Catastale">
                    <div className="md:col-span-2">
                        <CadastreSection propertyId={mode === "edit" ? id : null} onDraftChange={setCadastreDraft} refreshSignal={docVersion} />
                    </div>
                </Section>

                {/* Documentazione Immobile (comprimibile) */}
                <div className="border border-white/10 bg-ink-900/20">
                    <button type="button" data-testid="docs-accordion-toggle" onClick={() => setDocsOpen(!docsOpen)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors">
                        <span className="font-serif text-xl text-ink-50">📂 Documentazione Immobile</span>
                        <span className="flex items-center gap-2 text-[11px] text-ink-400">
                            Certificazioni, catasto, urbanistica, proprietà, tecnica, contratti
                            {docsOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </span>
                    </button>
                    {docsOpen && (
                        <div className="px-5 pb-5 border-t border-white/10 pt-5">
                            <DocumentsManager propertyId={mode === "edit" ? id : null} pendingDocs={pendingDocs} setPendingDocs={setPendingDocs} onChanged={() => setDocVersion((v) => v + 1)} />
                        </div>
                    )}
                </div>

                {error && <div data-testid="form-error" className="text-red-400 text-sm">{error}</div>}

                {/* Privacy contact reminder — visible right before publish button. Gold accent to attract attention without invasiveness. */}
                <div
                    data-testid="privacy-reminder-box"
                    className={`border-2 p-5 md:p-6 rounded-sm transition-colors ${user?.contacts_public
                        ? "border-blue-500/50 bg-gradient-to-br from-blue-500/10 to-blue-500/5"
                        : "border-gold/50 bg-gradient-to-br from-gold/10 to-gold/5"}`}
                >
                    <div className="flex items-start gap-3">
                        <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${user?.contacts_public ? "bg-blue-500/20" : "bg-gold/20"}`}>
                            {user?.contacts_public
                                ? <Globe size={18} className="text-blue-300" />
                                : <ShieldCheck size={18} className="text-gold" />}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="text-[11px] uppercase tracking-wider text-ink-400">Privacy contatti</span>
                                <span
                                    data-testid="privacy-status-badge"
                                    className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${user?.contacts_public
                                        ? "bg-blue-500/20 text-blue-300"
                                        : "bg-gold/20 text-gold"}`}
                                >
                                    I tuoi contatti saranno: {user?.contacts_public ? "PUBBLICI" : "PRIVATI"}
                                </span>
                            </div>
                            <p className="text-sm text-ink-100 leading-relaxed mt-1">
                                {user?.contacts_public
                                    ? "Telefono ed email saranno visibili direttamente nell'annuncio. Gli interessati potranno chiamarti subito."
                                    : "Vuoi che i clienti ti chiamino subito? Rendi i tuoi dati pubblici per questo e per i prossimi annunci."}
                            </p>
                        </div>
                        {/* Quick toggle switch — pill style, mobile-friendly */}
                        <button
                            data-testid="privacy-quick-toggle"
                            type="button"
                            onClick={() => togglePrivacy(!user?.contacts_public)}
                            disabled={privacyBusy}
                            aria-label={user?.contacts_public ? "Rendi contatti privati" : "Rendi contatti pubblici"}
                            className={`shrink-0 relative w-14 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ink-950 ${user?.contacts_public ? "bg-blue-500 focus:ring-blue-400" : "bg-ink-700 focus:ring-gold/60"} disabled:opacity-50`}
                        >
                            <span
                                className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform flex items-center justify-center ${user?.contacts_public ? "translate-x-6" : "translate-x-0"}`}
                            >
                                {user?.contacts_public
                                    ? <Globe size={12} className="text-blue-500" />
                                    : <Lock size={12} className="text-ink-700" />}
                            </span>
                        </button>
                    </div>
                    <p className="text-[11px] text-ink-500 mt-3 pl-13 md:pl-13">
                        Puoi cambiare questa impostazione in qualsiasi momento dalla sezione <b>Il mio profilo</b> della Dashboard.
                    </p>
                </div>

                <button data-testid="submit-property" type="submit" disabled={submitting} className="w-full md:w-auto px-12 py-4 bg-gold text-black font-medium hover:bg-gold-soft transition-colors disabled:opacity-50">
                    {submitting ? "Salvataggio..." : (mode === "edit" ? "Salva modifiche" : "Pubblica annuncio")}
                </button>
            </form>

            {photoTourPreview && form.photos.length >= 3 && (
                <PhotoTour
                    photos={form.photos}
                    title={form.title || "Anteprima"}
                    onClose={() => setPhotoTourPreview(false)}
                    fullscreen
                />
            )}
        </div>
    );
}
